import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFieldSalesMatrix } from '../hooks/useFieldSalesDashboard.js';
import {
  formatValueAuto,
  formatIndianInt,
  achDelta,
  deltaClass,
  DELTA_COLOR,
  FS_HEAD_SX,
  FS_CELL_SX,
} from './fieldSalesFormat.js';
import FieldSalesPager from './FieldSalesPager.jsx';
import { usePagedRows } from './fieldSalesPaging.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';
const GOOD = '#16A34A';
const PRIMARY = '#2E7CF6';
const WARN = '#F59E0B';
const BAD = '#DC2626';
const SHADOW = '0 1px 2px rgba(15,30,61,.04), 0 4px 18px rgba(15,30,61,.06)';
const NO_DATA_TOOLTIP = 'No data source yet';

// Reference QUAD titles + action text; the task asks for FAINT quadrant fills
// (HH green, HL blue, LH orange, LL red), so each uses a light tint + its tone
// colour for text/accent rather than the HTML's solid gradient.
const QUAD = {
  HH: { t: 'High PCPM · High Growth', name: '★ Stars — protect & replicate', tone: GOOD, bg: 'rgba(22,163,74,.12)' },
  HL: { t: 'High PCPM · Low Growth', name: 'Defend — productive but slowing', tone: PRIMARY, bg: 'rgba(46,124,246,.12)' },
  LH: { t: 'Low PCPM · High Growth', name: 'Develop — emerging, low base', tone: WARN, bg: 'rgba(245,158,11,.16)' },
  LL: { t: 'Low PCPM · Low Growth', name: 'Fix — low & declining', tone: BAD, bg: 'rgba(220,38,38,.12)' },
};
const QUAD_ORDER = ['HH', 'HL', 'LH', 'LL'];

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** Greyed Zone cell: the backend returns the state there, so Zone has no source. */
function ZoneCell() {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.5, cursor: 'not-allowed' }}
      >
        —
        <Box
          component="span"
          sx={{
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

/** One faint quadrant card. */
function QuadCard({ q, selected, hqCount, tmCount, onClick }) {
  const m = QUAD[q];
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        textAlign: 'left',
        font: 'inherit',
        cursor: 'pointer',
        borderRadius: '13px',
        p: '13px 15px',
        bgcolor: m.bg,
        color: NAVY,
        border: `1px solid ${selected ? m.tone : 'transparent'}`,
        outline: selected ? `2px solid ${m.tone}` : 'none',
        outlineOffset: '1px',
        transition: 'transform .12s ease, box-shadow .12s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(15,30,61,.14)' },
      }}
    >
      <Box sx={{ fontSize: '12.5px', fontWeight: 800, letterSpacing: '.3px', color: m.tone }}>{m.t}</Box>
      <Box sx={{ fontSize: '10.5px', mt: '2px', color: MUTED }}>{m.name}</Box>
      <Box sx={{ fontSize: '17px', fontWeight: 800, mt: '7px', color: NAVY }}>
        {formatIndianInt(hqCount)} HQ · {formatIndianInt(tmCount)} TM
      </Box>
    </Box>
  );
}

/**
 * PCPM × Growth Matrix tab (reference "PCPM × Growth Matrix — Action Buckets").
 * Four faint clickable quadrants with HQ/TM counts from counts_hq/counts_tm and
 * a thresholds note. Clicking a quadrant loads /matrix?quad=… and lists that
 * quadrant's HQs in a panel with a Total row. Zone is a greyed "—". Values in
 * Lakhs → ₹ Cr/L.
 */
export default function FieldSalesMatrixTab() {
  const [quad, setQuad] = useState('HH');
  const query = useFieldSalesMatrix(quad);
  const data = query.data;
  const countsHq = data?.countsHq ?? {};
  const countsTm = data?.countsTm ?? {};
  const thresholds = data?.thresholds ?? {};
  const rows = data?.rows ?? [];

  const loading = query.isLoading || query.isFetching;
  const multiDiv = new Set(rows.map((r) => r.division)).size > 1;

  // Client-side pagination (50/page); resets when the quadrant or row count changes.
  const paged = usePagedRows(rows, { resetKey: `${quad}:${rows.length}` });

  // Totals for the selected quadrant's panel.
  const tTm = rows.reduce((s, r) => s + (Number(r.tm) || 0), 0);
  const tTgt = rows.reduce((s, r) => s + (Number(r.target) || 0), 0);
  const tSal = rows.reduce((s, r) => s + (Number(r.sales) || 0), 0);
  const tAch = tTgt > 0 ? (tSal / tTgt) * 100 : NaN;

  const pcpmThresh = Number(thresholds.pcpm_tm);
  const growthThresh = Number(thresholds.growth);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        boxShadow: SHADOW,
        p: '16px 18px',
        mt: 1,
      }}
    >
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: NAVY, mb: '4px' }}>
        ▦ PCPM × Growth Matrix — Action Buckets
      </Typography>
      <Typography sx={{ fontSize: '12px', color: MUTED, mb: '12px' }}>
        Vs division average — PCPM/TM ={' '}
        <b>{Number.isFinite(pcpmThresh) ? formatValueAuto(pcpmThresh) : '—'}</b>, growth split at{' '}
        <b>{Number.isFinite(growthThresh) ? `${growthThresh.toFixed(1)}%` : '—'}</b>. Click a quadrant to
        list its HQs. HQs without a prior-year base are excluded.
      </Typography>

      {query.isError ? (
        <Alert severity="error">Failed to load the matrix.</Alert>
      ) : (
        <>
          {/* Four faint quadrant cards. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' },
              gap: '10px',
              mb: '14px',
            }}
          >
            {QUAD_ORDER.map((q) => (
              <QuadCard
                key={q}
                q={q}
                selected={quad === q}
                hqCount={countsHq[q]}
                tmCount={countsTm[q]}
                onClick={() => setQuad(q)}
              />
            ))}
          </Box>

          {/* Selected quadrant panel. */}
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: NAVY, mb: '6px' }}>
            {QUAD[quad].t} — <b>{formatIndianInt(rows.length)}</b> HQs · <b>{formatIndianInt(tTm)}</b> TMs
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              No HQs in this quadrant for the current filters.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 480, border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_SX}>#</TableCell>
                    <TableCell sx={HEAD_SX}>HQ</TableCell>
                    {multiDiv ? <TableCell sx={HEAD_SX}>Division</TableCell> : null}
                    <TableCell sx={HEAD_SX}>Zone</TableCell>
                    <TableCell sx={HEAD_SX} align="right">TMs</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Target</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Sales</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Ach %</TableCell>
                    <TableCell sx={HEAD_SX} align="right">PCPM/TM</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Growth %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.pageRows.map((r, i) => (
                    <TableRow key={`${r.hq}-${r.division}-${i}`} hover>
                      <TableCell sx={CELL_SX}>{paged.offset + i + 1}</TableCell>
                      <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{r.hq ?? '—'}</TableCell>
                      {multiDiv ? <TableCell sx={CELL_SX}>{r.division ?? '—'}</TableCell> : null}
                      <TableCell sx={CELL_SX}><ZoneCell /></TableCell>
                      <TableCell sx={CELL_SX} align="right">{formatIndianInt(r.tm)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.target)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.sales)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        <Box component="span" sx={{ color: DELTA_COLOR[achDelta(r.ach)] }}>
                          {Number.isFinite(Number(r.ach)) ? `${Number(r.ach).toFixed(1)}%` : '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        {Number.isFinite(Number(r.pcpm_tm)) ? formatValueAuto(r.pcpm_tm) : '—'}
                      </TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        <Box component="span" sx={{ color: DELTA_COLOR[deltaClass(r.growth)] }}>
                          {Number.isFinite(Number(r.growth))
                            ? `${Number(r.growth) > 0 ? '+' : ''}${Number(r.growth).toFixed(1)}%`
                            : '—'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { bgcolor: '#f8fafc', fontWeight: 700, borderTop: `2px solid ${BORDER}` } }}>
                    <TableCell sx={CELL_SX} colSpan={multiDiv ? 4 : 3}>
                      Total · {formatIndianInt(rows.length)} HQ{rows.length === 1 ? '' : 's'}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{formatIndianInt(tTm)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(tTgt)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(tSal)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box component="span" sx={{ color: DELTA_COLOR[achDelta(tAch)] }}>
                        {Number.isFinite(tAch) ? `${tAch.toFixed(1)}%` : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right" />
                    <TableCell sx={CELL_SX} align="right" />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && rows.length > 0 ? (
            <FieldSalesPager page={paged.page} pageCount={paged.pageCount} total={paged.total} onChange={paged.setPage} unit="HQs" />
          ) : null}
        </>
      )}
    </Box>
  );
}
